import {
    DetailsListLayoutMode,
    IColumn, IContextualMenuItem, Modal, Selection, SelectionMode
} from '@fluentui/react';
import { RootState } from 'app/store';
import CustomPanel from 'components/controls/CustomPanel';
import { filterPredicateColumnFAM, filterPredicateColumnPropertyName, filterPredicateColumnPropertyString, onRenderColumnFAM, onRenderColumnRef, sortPredicateColumnFAM, sortPredicateColumnPropertyName } from 'components/controls/Grid';
import { EControlType } from 'components/ControlWrapper';
import { bcbs_config_attributeid_BCCAdmin, bcbs_config_attributeid_FeeList, createAttributeSet, emitCustomEvent } from 'global/Application';
import { autoGenerateFeesAndPremiumsFeesAction } from 'global/Application/dataActions';
import { addUpdateAttributeSet, IAttributeSetPayload } from 'global/Reducer/dataSlice';
import {
    IBenefit as FAM_IBenefit,
    IEntityReferenceValue as FAM_IEntityReferenceValue,
    IFeesAndPremiums as FAM_IFeesAndPremiums,
    IFlexibleFieldSet as FAM_IFlexibleFieldSet
} from 'model/FlexibleAttributeModel';
import { IGroupConfigurationData } from "model/FlexibleAttributeModel";
import { useMemo, useRef, useState } from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import AttributeSetGridViewEditor from './AttributeSetGridViewEditor';
import FeesAndPremiumWizard from './FeesAndPremiumWizard';

import * as CRMOptionSet from "model/AttributeOptionSets.type";

import CustomDetailList from 'components/controls/CustomDetailList';
import { DefaultGridConfig, UpdateColumnByConfig } from 'components/controls/CustomDetailList/CustomDetailList';
import { ICustomDetailListConfiguration } from 'components/controls/CustomDetailList/ICustomDetailListConfiguration';
import { IGridItem } from 'components/controls/CustomDetailList/IGridItem';
import { useAuthentication } from 'global/Authentication';
import { setProcessing, updateCustomDetailListConfiguration } from 'global/Reducer/statusSlice';
import { IExtendedColumn } from 'model/IExtendedColumn';
import { StickyDetailsList } from "../../components/layout/StickyDetailList";
import { IVisibilityScope } from 'global/Visibility/IVisibilityScope.type';
import saveAs from 'file-saver';
import { AppDataSource } from 'global/DataSources/AppDataSource';
import { MsalTokenSource } from 'global/DataSources/MsalTokenSource';
import { useMsal } from '@azure/msal-react';
import FeesAndPremiumExportImportWizard from './FeesAndPremiumExportImportWizard';
import { Constants } from 'global/Application/Constants';
import { $matt_$fees_and_premiums_$bcbs_attributeset_credit_id, $matt_$fees_and_premiums_$bcbs_attributeset_fee_id } from "model/MasterAttributeConstants";
import { $att_$fees_and_premiums_$bcbs_attributeset_credit_id, $att_$fees_and_premiums_$bcbs_attributeset_fee_id } from "model/ConfigAttributeConstants";


interface IProps extends TComponentConnectedProps {
    onPick?: (index: number) => void;
    snap?: IGroupConfigurationData;
    disabled?: boolean;
    feesAndPremium?: FAM_IFeesAndPremiums;
    creditHidden: boolean;
    visibility?: IVisibilityScope;
}

const FeesGrid = (props: IProps) => {
    const { authorizations } = useAuthentication();

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const config = props.state.data.config
    const feesAndPremiums: FAM_IFeesAndPremiums[] = props.state.data.json.feesAndPremiums

    const isNationalAlliance = props.state.data.json.lineOfBusiness?.value === CRMOptionSet.account.bcbs_lineofbusiness["National Alliance(808900001)"].value;

    let disabled = props.disabled;

    if (isNationalAlliance)
        if (!authorizations?.roles["BCBS - Power User"])
            disabled = true;

    const [ffs, setFFS] = useState<FAM_IFlexibleFieldSet | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const dispatch = useDispatch();

    const storageId = `fees.grid`;

    const scopeGridConfig = () => {
        const u = props.state.status.gridConfigs[storageId];
        if (u)
            return u;

        const t = DefaultGridConfig();
        // t.sort = [{ column: { key: 'attributeSetName' }, isSortedDescending: false }];

        return t;
    }

    const [gridConfig, setGridConfig] = useState<ICustomDetailListConfiguration>(scopeGridConfig());
    const gridConfigRef = useRef(gridConfig);

    // check if it has changed since mounting
    if (gridConfig !== gridConfigRef.current) {
        // console.log("render:grid config updated", gridConfig);

        gridConfigRef.current = gridConfig;

        // snapshot for bind back
        dispatch(updateCustomDetailListConfiguration({ id: storageId, gridConfig: structuredClone(gridConfig) }));
    }

    const columnsFee = useMemo(() => {

        const onColumnClick = (event: React.MouseEvent<HTMLElement, MouseEvent>, column: IColumn) => {
            setGridConfig(t => {

                const gridConfig: ICustomDetailListConfiguration = { ...t };

                gridConfig.sort = [{
                    column: { key: column.key },
                    isSortedDescending: !t?.sort?.[0]?.isSortedDescending,
                }];

                return gridConfig;
            });
        };

        const columns: IExtendedColumn[] = [];

        if (!props.feesAndPremium) {
            const column: IExtendedColumn = {
                key: 'fnpName',
                name: 'Fees And Premium',
                fieldName: 'feesAndPremiumRef',
                minWidth: 185,
                isResizable: true,
                onRender: onRenderColumnRef
            }

            column.onColumnClick = onColumnClick;
            UpdateColumnByConfig(column, gridConfig);
            column.sortPredicate = sortPredicateColumnPropertyName;
            column.filterPredicate = filterPredicateColumnPropertyName;

            columns.push(column);
        }

        config?.attributes.forEach(eachAttribute => {

            // Fee attribute, not sure why this was done
            // https://bcbsonlinedevgl.crm.dynamics.com/main.aspx?appid=bcf569be-d29b-ec11-b400-000d3a5b9435&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=f5646443-d590-ec11-b400-0022480a93ed
            if (eachAttribute._bcbs_masterattributesetid_value !== $matt_$fees_and_premiums_$bcbs_attributeset_fee_id)
                return;

            const configMasterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[eachAttribute._bcbs_config_masterattributeid_value!];

            const column: IExtendedColumn = {
                key: eachAttribute.bcbs_config_attributeid!,
                name: eachAttribute.bcbs_name!,
                minWidth: eachAttribute.bcbs_viewcolumnwidth ?? (eachAttribute?.bcbs_name?.length ?? 10) * 8,
                fieldName: eachAttribute.bcbs_config_attributeid,
                isResizable: true,
                data: eachAttribute,
                onRender: onRenderColumnFAM(configMasterAttribute.bcbs_attributetypecode as EControlType),
            };

            // Custom Sort Registration
            {
                column.onColumnClick = onColumnClick;
                UpdateColumnByConfig(column, gridConfig);
                column.sortPredicate = sortPredicateColumnFAM(configMasterAttribute.bcbs_attributetypecode as EControlType);
                column.filterPredicate = filterPredicateColumnFAM(configMasterAttribute.bcbs_attributetypecode as EControlType);
            }

            columns.push(column);
        });

        // Name Column
        {
            const column: IExtendedColumn = {
                key: 'attributeSetName',
                name: 'Name',
                fieldName: 'name',
                isResizable: true,
                minWidth: 140,
                data: { bcbs_viewsortorder: Number.MAX_SAFE_INTEGER }
            }

            column.onColumnClick = onColumnClick;
            UpdateColumnByConfig(column, gridConfig);
            column.sortPredicate = sortPredicateColumnPropertyName;
            column.filterPredicate = filterPredicateColumnPropertyString;

            columns.push(column);
        }


        return columns.sort((a1, a2) => a1.data?.bcbs_viewsortorder! - a2.data?.bcbs_viewsortorder!);
    },
        [
            gridConfig
        ]
    );

    const columnsCredit = useMemo(() => {

        const columns: IColumn[] = [];
        columns.push({
            key: 'fnpName',
            name: 'Fees And Premium',
            fieldName: 'feeAndPremiumName',
            minWidth: 185,
            isResizable: true,
        })

        config?.attributes.forEach(eachAttribute => {
            if (eachAttribute._bcbs_masterattributesetid_value !== $matt_$fees_and_premiums_$bcbs_attributeset_credit_id)
                return;

            const eachAttributeMaster = props.state.data.config.cache.map_bcbs_config_masterattribute[eachAttribute._bcbs_config_masterattributeid_value!];

            const column: IColumn = {
                key: eachAttribute.bcbs_config_attributeid!,
                name: eachAttribute.bcbs_name!,
                minWidth: eachAttribute.bcbs_viewcolumnwidth ?? (eachAttribute?.bcbs_name?.length ?? 10) * 8,
                fieldName: eachAttribute.bcbs_config_attributeid,
                isResizable: true,
                data: eachAttribute,
                onRender: onRenderColumnFAM(eachAttributeMaster.bcbs_attributetypecode as EControlType),
            };

            columns.push(column);
        });




        return columns.sort((a1, a2) => a1.data?.bcbs_viewsortorder! - a2.data?.bcbs_viewsortorder!);

    }, [gridConfig]);

    const [itemsFee, itemsFeePaged] = useMemo(() => {
        let items: IGridItem<FAM_IFlexibleFieldSet>[] = [];

        const gridConfigMappedColumns = {
            filterColumns: gridConfig.filter.map(q => columnsFee?.find(w => w.key === q.column.key)),
            sortColumns: gridConfig.sort.map(q => columnsFee?.find(w => w.key === q.column.key)),
        }

        function filterPredicate(feesAndPremium: any, fattributeSet: any, item: IGridItem<FAM_IFlexibleFieldSet>) {

            if (!item)
                return false;

            if (!props.feesAndPremium)
                return true;

            for (const id of bcbs_config_attributeid_FeeList) {
                if (item[id]) {
                    return true;
                }
            }

            return false;
        }

        // fee config:
        const attributeFee = props.state.data.config.cache.map_bcbs_config_attribute[$att_$fees_and_premiums_$bcbs_attributeset_fee_id];

        feesAndPremiums.forEach((eachFeeAndPremium, index) => {

            if ((eachFeeAndPremium.deactivated ?? false) !== props.state.data.json.showInActives?.value)
                return;

            if (props.feesAndPremium) {
                if (eachFeeAndPremium.id !== props.feesAndPremium.id) {
                    return;
                }
            }

            eachFeeAndPremium.attributeSets.forEach((eachAttributeSet, index) => {

                if ((eachAttributeSet.deactivated ?? false) !== props.state.data.json.showInActives?.value)
                    return;

                // Pick only FEE attribute set
                if (eachAttributeSet.attributeSetRef.id !== attributeFee._bcbs_config_masterattributeid_value)
                    return;

                const item: IGridItem<FAM_IFlexibleFieldSet> = {
                    _index: index,
                    key: eachAttributeSet.id,
                    name: eachAttributeSet.name,
                    // feeAndPremiumName: { type: 'string', value: eachFeeAndPremium.name },
                    feesAndPremiumRef: { id: eachFeeAndPremium.id, name: eachFeeAndPremium.name },
                    data: eachAttributeSet,
                }

                eachAttributeSet.fields.forEach(eachField => {

                    const fieldName = eachField.configRef.id;

                    // lets always get the first one
                    // verified with William there will never be a Tier/PCP/Specialist mode for these.
                    const field = eachField.values[0];
                    item[fieldName] = field.value;

                });


                // Filtering :
                let include = true;
                if (gridConfig && gridConfig.filter && gridConfig.filter.length > 0) {

                    gridConfig.filter.forEach((filter, ix) => {
                        const column = gridConfigMappedColumns.filterColumns[ix];
                        if (column) {
                            const predicate = column.filterPredicate;
                            if (predicate)
                                include = include && predicate(item, filter.value, column);
                        }
                    });
                }

                if (include)
                    if (filterPredicate) {
                        if (filterPredicate(eachFeeAndPremium, eachAttributeSet, item))
                            items.push(item);
                    }
                    else {
                        items.push(item);
                    }

            });
        })

        // Sorting :
        if (gridConfig && gridConfig.sort && gridConfig.sort.length > 0)
            items.sort((a, b) => {

                let ret = 0;

                gridConfig.sort?.forEach((sort, ix) => {
                    const column = gridConfigMappedColumns.sortColumns[ix];

                    // TODO why was this needed?
                    if (!column)
                        return;

                    const predicate = column.sortPredicate;
                    const isSortedDescending = sort.isSortedDescending ?? false;

                    if (predicate)
                        ret = isSortedDescending
                            ? predicate(a, b, undefined, column) * -1
                            : predicate(a, b, undefined, column);
                });

                return ret;

            });

        const itemsPaged = items.slice((gridConfig.page - 1) * gridConfig.pageSize, gridConfig.page * gridConfig.pageSize);

        return [items, itemsPaged];
    },
        [
            props.state.data.json.feesAndPremiums,
            gridConfig,
        ]
    );

    const itemsCredit = useMemo(() => {
        let items: IGridItem<FAM_IFlexibleFieldSet>[] = []

        // credit :
        const attributeCredit = props.state.data.config.cache.map_bcbs_config_attribute[$att_$fees_and_premiums_$bcbs_attributeset_credit_id];

        feesAndPremiums.forEach((eachFeeAndPremium, index) => {

            eachFeeAndPremium.attributeSets.forEach((eachAttributeSet, index) => {
                // Pick only FEE attribute set
                if (eachAttributeSet.attributeSetRef.id !== attributeCredit._bcbs_config_masterattributeid_value)
                    return;

                const item: IGridItem<FAM_IFlexibleFieldSet> = {
                    _index: index,
                    key: eachFeeAndPremium.id,
                    name: eachFeeAndPremium.name,
                    feeAndPremiumName: eachFeeAndPremium.name,
                    data: eachAttributeSet,
                }

                eachAttributeSet.fields.forEach(eachField => {

                    const fieldName = eachField.configRef.id;

                    // lets always get the first one
                    // verified with William there will never be a Tier/PCP/Specialist mode for these.
                    const field = eachField.values[0];
                    item[fieldName] = field.value;
                });

                item['feeAndPremiumName'] = eachFeeAndPremium.name

                items.push(item)
            })
        })
        return items

    }, [props.state.data.json.feesAndPremiums])

    const [canEdit, setCanEdit] = useState(false);

    const [selectedItems, setSelectedItems] = useState<FAM_IFlexibleFieldSet[] | null>(null);
    const selection = useMemo(() => {
        return new Selection({
            onSelectionChanged: () => {
                if (selection.getSelectedCount() > 0)
                    setCanEdit(true)
                else
                    setCanEdit(false)
            },
            selectionMode: SelectionMode.multiple,
        })
    }, []);

    const onSelect = (selectedItems: FAM_IFlexibleFieldSet[], action: 'edit' | 'copy') => {
        if (selectedItems) {
            setSelectedItems(selectedItems);

            if (action === 'edit') {
                if (selectedItems.length === 1) {
                    const _ffs = selectedItems[0];
                    // clone in case we opt out of changing, to build a local state and disconnect from store
                    setFFS(structuredClone(_ffs));
                    setIsOpen(true);
                } else if (selectedItems.length > 1) {
                    const feeAttribute = props.state.data.config.cache.map_bcbs_config_attribute[$att_$fees_and_premiums_$bcbs_attributeset_fee_id];
                    const feesMasterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[feeAttribute._bcbs_config_masterattributeid_value!];
                    const _ffs = createAttributeSet(props.state.data, feesMasterAttribute!);
                    _ffs.id = 'bulk';
                    setFFS(_ffs);
                    setIsOpen(true);
                }
            } else if (action === 'copy') {
                setIsOpenFP(true);
            }
        }
    }

    const menu: IContextualMenuItem[] = [];
    if (!disabled)
        if (!props.feesAndPremium)
            menu.push({
                key: `feeGenerate`,
                name: 'Generate',
                icon: 'AutoEnhanceOn',
                onClick: (event, item) => {
                    if (event?.stopPropagation)
                        event.stopPropagation();

                    dispatch(autoGenerateFeesAndPremiumsFeesAction());
                }
            });

    if (!disabled)
        if (!props.feesAndPremium)
            menu.push({
                key: 'ManageFeesExcel',
                name: 'Manage Fees',
                icon: 'Generate',
                onClick: (event, item) => {
                    if (event?.stopPropagation)
                        event.stopPropagation();

                    //open up exportImportWizard
                    //setIsEditOpen(true)
                    const selectedItems = selection.getSelection() as IGridItem<FAM_IFlexibleFieldSet>[];
                    onSelect(selectedItems.map(q => q.data!), 'copy');
                }
            });

    if (!disabled)
        if (canEdit)
            menu.push({
                key: `feeCopy`,
                name: 'Add To',
                icon: 'Copy',
                onClick: (event, item) => {
                    if (event?.stopPropagation)
                        event.stopPropagation();

                    const selectedItems = selection.getSelection() as IGridItem<FAM_IFlexibleFieldSet>[];
                    onSelect(selectedItems.map(q => q.data!), 'copy');
                }
            });

    if (!disabled)
        if (canEdit)
            menu.push({
                key: `feeEdit`,
                name: 'Edit',
                icon: 'PageEdit',
                onClick: (event, item) => {
                    if (event?.stopPropagation)
                        event.stopPropagation();

                    const selectedItems = selection.getSelection() as IGridItem<FAM_IFlexibleFieldSet>[];
                    onSelect(selectedItems.map(q => q.data!), 'edit');
                },
            });

    const categoryFeesAndPremiums = props.state.data.config.cache.map_bcbs_config_category_named["Fees and Premiums"];

    const [isOpenFP, setIsOpenFP] = useState(false);

    return (
        <>
            {/* <FeesAndPremiumExportImportWizard
                            isOpen = {isEditOpen}
                            onDismiss={() => setIsEditOpen(false)}
                            onCancel={() => setIsEditOpen(false)}
                            variant='export'
                            embedded={false}/> */}
            <FeesAndPremiumWizard
                isOpen={isOpenFP}
                onCancel={() => { setIsOpenFP(false); }}
                onDismiss={() => { setIsOpenFP(false); }}
                variant={'select'}
                onConfirm={(e) => {
                    setIsOpenFP(false);

                    const targetFP = e?.value.feesAndPremiums;

                    const change = [] as IAttributeSetPayload[]

                    targetFP?.forEach(eTargetFP => {
                        selectedItems?.forEach(eSelectedItem => {

                            let foundAttSet = false;
                            let foundMatchingBccAdmin = false;

                            const selectedItemBccAdmin = eSelectedItem.fields.find(q => q.configRef.id === bcbs_config_attributeid_BCCAdmin);

                            // checking all attributeSets of each target to make sure it
                            // a) it does not contain this attribute set that I am about to copy
                            // b) it does not contain an attribute set that has the same as bccAdmin as the one that I am about to copy 
                            // * b kind of makes a pointless, but should add a lot of performance
                            eTargetFP.attributeSets.every(eachAttributeSet => {

                                // Attribute Set Check
                                // ------------------
                                if (eachAttributeSet.id === eSelectedItem.id) {
                                    foundAttSet = true;

                                    // stop looping
                                    return false;
                                }

                                // BCC admin Check 
                                {
                                    const eachAttributeSetBccAdmin = eachAttributeSet.fields.find(q => q.configRef.id === bcbs_config_attributeid_BCCAdmin);
                                    const bccAdmin1 = eachAttributeSetBccAdmin?.values?.[0]?.value as FAM_IEntityReferenceValue;
                                    const bccAdmin2 = selectedItemBccAdmin?.values?.[0]?.value as FAM_IEntityReferenceValue;
                                    if (bccAdmin1?.id === bccAdmin2?.id) {
                                        foundMatchingBccAdmin = true;

                                        // stop looping
                                        return false;
                                    }
                                }

                                // keep looping
                                return true;
                            })

                            change.every(eachChange => {
                                eachChange.attributeSets.every(eachAttributeSet => {
                                    // BCC admin Check 
                                    {
                                        const eachAttributeSetBccAdmin = eachAttributeSet.fields.find(q => q.configRef.id === bcbs_config_attributeid_BCCAdmin);
                                        const bccAdmin1 = eachAttributeSetBccAdmin?.values?.[0]?.value as FAM_IEntityReferenceValue;
                                        const bccAdmin2 = selectedItemBccAdmin?.values?.[0]?.value as FAM_IEntityReferenceValue;
                                        if (bccAdmin1?.id === bccAdmin2?.id) {
                                            foundMatchingBccAdmin = true;

                                            // stop looping
                                            return false;
                                        }
                                    }

                                    return true;

                                });

                                return true;
                            });

                            // if we found the attribute set, nothing to do, its probably selected from same attribute set
                            if (foundAttSet)
                                return;

                            if (foundMatchingBccAdmin)
                                return;

                            const clone = structuredClone(eSelectedItem);

                            // re-assigning clone of the attributes
                            clone.id = uuidv4();
                            clone.fields.forEach(eachField => {
                                clone.id = uuidv4();
                            });

                            change.push({
                                category: categoryFeesAndPremiums as FAM_IBenefit,
                                scope: eTargetFP,
                                attributeSets: [clone],
                                config: props.state.data.config,
                                changeTracker: {}
                            });

                        });
                    });

                    dispatch(addUpdateAttributeSet(change));
                }}
            />

            <AttributeSetGridViewEditor
                isOpen={isOpen}
                onDismiss={() => { setIsOpen(false); }}
                category={categoryFeesAndPremiums}
                disabled={disabled}
                defaultValue={ffs}
                visibility={props.visibility}
                onChange={(attributeSet, changeTracker) => {

                    const update = [] as IAttributeSetPayload[];

                    if (attributeSet.id !== 'bulk') {

                        props.state.data.json.feesAndPremiums.forEach(eachFeesAndPremium => {
                            eachFeesAndPremium.attributeSets.forEach(eachAttributeSet => {

                                if (eachAttributeSet.id === attributeSet.id) {
                                    update.push({
                                        category: categoryFeesAndPremiums as FAM_IBenefit,
                                        scope: eachFeesAndPremium,
                                        attributeSets: [attributeSet],
                                        config: props.state.data.config,
                                        changeTracker: changeTracker
                                    });
                                }
                            });
                        });
                    }
                    else {
                        if (!selectedItems)
                            return;

                        props.state.data.json.feesAndPremiums.forEach(eachFeesAndPremium => {
                            eachFeesAndPremium.attributeSets.forEach(eachAttributeSet => {

                                selectedItems.forEach(eachSelectedItem => {
                                    if (eachAttributeSet.id === eachSelectedItem.id) {
                                        const clone = structuredClone(eachAttributeSet);

                                        clone.fields.forEach(eField => {
                                            const matchingField = attributeSet.fields.find(q => q.configRef.id === eField.configRef.id);

                                            if (matchingField) {
                                                const value = matchingField.values[0].value;
                                                if (value) {
                                                    eField.values[0].value = value;
                                                }
                                            }
                                        });

                                        update.push({
                                            category: categoryFeesAndPremiums as FAM_IBenefit,
                                            scope: eachFeesAndPremium,
                                            attributeSets: [clone],
                                            config: props.state.data.config,
                                            changeTracker: changeTracker
                                        });
                                    }
                                });
                            })
                        });

                    }
                    dispatch(addUpdateAttributeSet(update));

                    setIsOpen(false);
                }}
            />
            
            <CustomPanel title={props.feesAndPremium ? 'BCC Fee Associated View' : 'Fees'}
                canCollapse={true}
                defaultCollapsed={true}
                storageId={"fees.fees"}
                items={menu}
            >
                <CustomDetailList
                    columns={columnsFee}
                    items={itemsFee}
                    itemsPaged={itemsFeePaged}
                    selectionMode={SelectionMode.multiple}
                    selection={selection}
                    getKey={(item, index) => item.key}
                    defaultGridConfig={gridConfig}
                    layoutMode={DetailsListLayoutMode.fixedColumns}
                    quickSearchColumnKey='attributeSetName'
                    gridConfig={[gridConfig, setGridConfig]}
                    onItemInvoked={(item: IGridItem<FAM_IFlexibleFieldSet>, index, event) => {

                        // to wait for render cycle to kick in first
                        // to open the right item, click was not passing in the right selected item.
                        setTimeout(() => {
                            const selectedItems = selection.getSelection() as IGridItem<FAM_IFlexibleFieldSet>[];
                            onSelect(selectedItems.map(q => q.data!), 'edit');
                        }, 60);
                    }}
                />
            </CustomPanel>

            {props.creditHidden === false &&
                <CustomPanel title='Credits'
                    canCollapse={true}
                    defaultCollapsed={true}
                    storageId={"fees.credits"}
                >
                    <StickyDetailsList
                        items={itemsCredit}
                        columns={columnsCredit}
                        compact={true}
                        selectionMode={SelectionMode.none}
                        layoutMode={DetailsListLayoutMode.fixedColumns}
                    />
                </CustomPanel>
            }
        </>
    )
}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(FeesGrid);
